/**
 * Structured Logger for React Frontend
 *
 * Provides consistent, structured logging with context, stack traces,
 * and environment-aware configuration. Supports sending logs to external
 * logging services (Sentry, LogRocket, or backend).
 *
 * @example
 * ```typescript
 * import { logger } from '@/shared/utils/logger';
 *
 * // Basic logging
 * logger.info('User logged in', { userId: '123', method: 'oauth' });
 *
 * // Error logging with context
 * logger.error('API request failed', {
 *   endpoint: '/api/v1/backups',
 *   status: 500,
 *   userId: '123'
 * }, error);
 *
 * // Debug logging (only in development)
 * logger.debug('Component state', { state: {...} });
 * ```
 */

// Log levels
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

// Log entry interface
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  userId?: string;
  component?: string;
  action?: string;
  environment: string;
  release?: string;
}

// Logger configuration
export interface LoggerConfig {
  level: LogLevel;
  environment: 'development' | 'staging' | 'production';
  enableConsole: boolean;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
  redactSensitiveData: boolean;
  includeStackTrace: boolean;
  release?: string;
}

// Default configuration
const defaultConfig: LoggerConfig = {
  level: LogLevel.INFO,
  environment: (import.meta.env.MODE as 'development' | 'staging' | 'production') || 'development',
  enableConsole: true,
  enableRemoteLogging: false,
  redactSensitiveData: true,
  includeStackTrace: true,
  release: import.meta.env.VITE_APP_VERSION || 'dev',
};

// Sensitive field patterns to redact
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /api[_-]?key/i,
  /authorization/i,
  /session/i,
  /cookie/i,
  /credit[_-]?card/i,
  /ssn/i,
  /social[_-]?security/i,
  /pin/i,
  /otp/i,
  /verification[_-]?code/i,
];

/**
 * Redact sensitive data from log context
 */
function redactSensitiveData(
  data: Record<string, unknown>,
  seen = new WeakSet()
): Record<string, unknown> {
  const redacted: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    // Check if key matches sensitive pattern
    const isSensitive = SENSITIVE_PATTERNS.some((pattern) => pattern.test(key));

    if (isSensitive) {
      redacted[key] = '[REDACTED]';
      continue;
    }

    // Handle nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (seen.has(value as object)) {
        redacted[key] = '[CIRCULAR]';
        continue;
      }
      seen.add(value as object);
      redacted[key] = redactSensitiveData(value as Record<string, unknown>, seen);
    } else if (Array.isArray(value)) {
      // Handle arrays (but don't recurse too deep)
      redacted[key] = value.map((item) =>
        item && typeof item === 'object'
          ? redactSensitiveData(item as Record<string, unknown>, seen)
          : item
      );
    } else {
      redacted[key] = value;
    }
  }

  return redacted;
}

/**
 * Format error for logging
 */
function formatError(error: unknown): LogEntry['error'] {
  if (!error) return undefined;

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === 'object' && error !== null) {
    return {
      name: (error as any).name || 'UnknownError',
      message: (error as any).message || String(error),
      stack: (error as any).stack,
    };
  }

  return {
    name: 'UnknownError',
    message: String(error),
  };
}

/**
 * Send log to remote logging service
 */
async function sendRemoteLog(entry: LogEntry, config: LoggerConfig): Promise<void> {
  if (!config.enableRemoteLogging || !config.remoteEndpoint) {
    return;
  }

  try {
    await fetch(config.remoteEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entry),
    });
  } catch (err) {
    // Fail silently to avoid infinite loops
    if (config.enableConsole) {
      console.error('[Logger] Failed to send remote log:', err);
    }
  }
}

/**
 * Get current user ID from context
 */
function getCurrentUserId(): string | undefined {
  // Try to get from localStorage (set by auth context)
  try {
    const userId = localStorage.getItem('userId');
    if (userId) return userId;
  } catch {
    // localStorage not available (e.g., in iframe with restrictions)
  }

  // Try to get from session storage
  try {
    const userId = sessionStorage.getItem('userId');
    if (userId) return userId;
  } catch {
    // sessionStorage not available
  }

  return undefined;
}

/**
 * Logger class
 */
class Logger {
  private config: LoggerConfig;
  private userId?: string;
  private component?: string;
  private action?: string;

  constructor(config: LoggerConfig = defaultConfig) {
    this.config = config;
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Set user context for all subsequent logs
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Clear user context
   */
  clearUserId(): void {
    this.userId = undefined;
  }

  /**
   * Set component context for all subsequent logs
   */
  setComponent(component: string): void {
    this.component = component;
  }

  /**
   * Clear component context
   */
  clearComponent(): void {
    this.component = undefined;
  }

  /**
   * Set action context for all subsequent logs
   */
  setAction(action: string): void {
    this.action = action;
  }

  /**
   * Clear action context
   */
  clearAction(): void {
    this.action = undefined;
  }

  /**
   * Create a logger with additional context
   */
  withContext(context: { userId?: string; component?: string; action?: string }): Logger {
    const child = new Logger(this.config);
    child.userId = context.userId || this.userId;
    child.component = context.component || this.component;
    child.action = context.action || this.action;
    return child;
  }

  /**
   * Internal log method
   */
  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: unknown
  ): void {
    // Check log level
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);

    if (messageLevelIndex < currentLevelIndex) {
      return; // Skip log based on level
    }

    // Build log entry
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      environment: this.config.environment,
      release: this.config.release,
    };

    // Add context
    if (context) {
      entry.context = this.config.redactSensitiveData ? redactSensitiveData(context) : context;
    }

    // Add user context
    const userId = this.userId || getCurrentUserId();
    if (userId) {
      entry.userId = userId;
    }

    // Add component/action context
    if (this.component) {
      entry.component = this.component;
    }
    if (this.action) {
      entry.action = this.action;
    }

    // Add error details
    if (error && this.config.includeStackTrace) {
      entry.error = formatError(error);
    }

    // Console logging
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }

    // Remote logging (async, non-blocking)
    if (this.config.enableRemoteLogging && level !== LogLevel.DEBUG) {
      // Don't send debug logs to remote service
      sendRemoteLog(entry, this.config).catch(() => {
        // Fail silently
      });
    }
  }

  /**
   * Log to console with formatting
   */
  private logToConsole(entry: LogEntry): void {
    const prefix = `[${entry.level.toUpperCase()}] [${entry.timestamp}]`;
    const contextStr = entry.context ? ` ${JSON.stringify(entry.context)}` : '';
    const userStr = entry.userId ? ` (user: ${entry.userId})` : '';
    const componentStr = entry.component ? ` [${entry.component}]` : '';
    const actionStr = entry.action ? ` ${entry.action}` : '';
    const message = `${prefix}${componentStr}${userStr}${actionStr}: ${entry.message}${contextStr}`;

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(message, entry.error || '');
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        console.info(message, entry.error || '');
        break;
      case LogLevel.WARN:
        // eslint-disable-next-line no-console
        console.warn(message, entry.error || '');
        break;
      case LogLevel.ERROR:
        // eslint-disable-next-line no-console
        console.error(message, entry.error || '');
        break;
    }
  }

  /**
   * Debug level log
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, context);
  }

  /**
   * Info level log
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, context);
  }

  /**
   * Warning level log
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, context);
  }

  /**
   * Error level log
   */
  error(message: string, error?: unknown, context?: Record<string, unknown>): void {
    this.log(LogLevel.ERROR, message, context, error);
  }
}

// Create singleton instance
export const logger = new Logger();

/**
 * Configure the global logger
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
  logger.configure(config);
}

/**
 * Get user ID for logging from auth context
 * Call this after user authentication
 */
export function setLoggerUserId(userId: string): void {
  logger.setUserId(userId);
  // Also store in localStorage for cross-session access
  try {
    localStorage.setItem('userId', userId);
  } catch {
    // localStorage not available
  }
}

/**
 * Clear user ID from logger
 */
export function clearLoggerUserId(): void {
  logger.clearUserId();
  try {
    localStorage.removeItem('userId');
  } catch {
    // localStorage not available
  }
}

export default logger;
